import { EField as EEpcField } from "@rentiohq/shared-frontend/dist/forms/epc/schema.epcInfo.types";
import {
  IValues,
  mapNullableBooleanValueToFormField,
} from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import {
  AMENITY_NAMES,
  getAmenityBooleanValue,
  getAmenityNumberValue,
  getAmenityStringValue,
  truncateEAN,
} from "@rentiohq/shared-frontend/dist/reduxV2/propertyAmenity";
import {
  EPropertyTypeId,
  IProperty,
} from "@rentiohq/shared-frontend/dist/types/property.types";
import { IPropertyAmenity } from "@rentiohq/shared-frontend/dist/types/propertyAmenity.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { EAN_LENGTH, EAN_PREFIX } from "./schema.editPropertyExtraInfo.config";
import { EField, IExtraData } from "./schema.editPropertyExtraInfo.types";

export const validationErrorsForEan = (ean = "") => {
  const result: string[] = [];

  if (ean === null || ean === "" || ean === EAN_PREFIX) {
    return result;
  }

  const lengthWithoutPrefix = EAN_LENGTH - EAN_PREFIX.length;

  if (ean.length > lengthWithoutPrefix) {
    result.push(
      getLocalizedText("system.form.error.text.exact_length", {
        value: `${EAN_LENGTH}`,
      }),
    );
  }

  if (ean.length < lengthWithoutPrefix) {
    result.push(
      getLocalizedText("system.form.error.text.exact_length", {
        value: `${EAN_LENGTH}`,
      }),
    );
  }

  return result;
};

/** A date amenity is stored as a string, so it needs parsing before a date
 * field can show it. An unparsable value is treated as absent rather than
 * handed to the picker, which would render "Invalid Date". */
export const parseAmenityDate = (value?: string | null) => {
  if (!value) {
    return undefined;
  }

  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? undefined : parsed;
};

export const getInitialValues = ({
  property,
  propertyAmenities,
}: IExtraData): IValues => {
  const amenities: IPropertyAmenity[] | undefined = propertyAmenities;
  const num = (name: string) => getAmenityNumberValue(amenities, name);
  const str = (name: string) => getAmenityStringValue(amenities, name);
  const bool = (name: string) =>
    mapNullableBooleanValueToFormField(getAmenityBooleanValue(amenities, name));

  return {
    // General
    [EField.Price]: property.price,
    [EField.Costs]: property.costs,
    [EField.CostsComment]: str(AMENITY_NAMES.costsComment),
    [EField.AvailableAtDate]: property.availableAtDate
      ? new Date(property.availableAtDate)
      : undefined,
    [EField.RoomCountBedroom]: num(AMENITY_NAMES.roomCountBedroom),
    [EField.RoomCountBathroom]: num(AMENITY_NAMES.roomCountBathroom),
    [EField.LivingSurfaceArea]: num(AMENITY_NAMES.livingSurfaceArea),
    [EField.BuildingFloor]: num(AMENITY_NAMES.buildingFloor),
    [EField.BuildingFloorCount]: num(AMENITY_NAMES.buildingFloorCount),

    // EPC
    [EEpcField.Label]: property.epcLabel,
    [EEpcField.Value]: property.epcValue,
    [EEpcField.ExpirationDate]: property.epcExpirationDate,
    [EEpcField.CertificateId]: property.epcCertificateId,
    [EField.ELevel]: num(AMENITY_NAMES.eLevel),
    [EField.KLevel]: num(AMENITY_NAMES.kLevel),

    // Bookkeeping
    [EField.CostUnit]: property.costUnit,
    [EField.BookkeepingPaidOutStartDate]: property.bookkeepingPaidOutStartDate,

    // Bookkeeping Group
    [EField.CostCenter]: property.costCenter,

    // Electricity
    [EField.ElectricityType]: property.electricityType,
    [EField.ElectricityEan]: truncateEAN(property.electricityEan),
    [EField.ElectricityMeterNumberBoth]: property.electricityMeterNumberBoth,
    [EField.ElectricityMeterType]: property.electricityMeterType,

    // Natural Gas
    [EField.NaturalGasAvailable]: property.naturalGasAvailable,
    [EField.NaturalGasEan]: truncateEAN(property.naturalGasEan),
    [EField.NaturalGasMeterNumber]: property.naturalGasMeterNumber,
    [EField.NaturalGasMeterType]: property.naturalGasMeterType,

    // Water
    [EField.WaterMeterNumber]: property.waterMeterNumber,

    // Exterior
    [EField.GroundSurfaceArea]: num(AMENITY_NAMES.groundSurfaceArea),
    [EField.BuildingSurfaceArea]: num(AMENITY_NAMES.buildingSurfaceArea),
    // Nobody has ever answered "is there a garden": until now the tab only
    // asked for the surface. A property that carries one is therefore read as
    // a yes, so an existing garden does not vanish behind an unanswered
    // question the first time this card is opened. The same for the terrace.
    [EField.HasGarden]: mapNullableBooleanValueToFormField(
      getAmenityBooleanValue(amenities, AMENITY_NAMES.hasGarden) ??
        (property.buildingGardenSurfaceArea ? true : null),
    ),
    [EField.BuildingGardenSurfaceArea]: property.buildingGardenSurfaceArea,
    [EField.GardenOrientation]: str(AMENITY_NAMES.gardenOrientation),
    [EField.HasTerrace]: mapNullableBooleanValueToFormField(
      getAmenityBooleanValue(amenities, AMENITY_NAMES.hasTerrace) ??
        (property.buildingTerraceSurfaceArea ? true : null),
    ),
    [EField.BuildingTerraceSurfaceArea]: property.buildingTerraceSurfaceArea,
    [EField.TerraceOrientation]: str(AMENITY_NAMES.terraceOrientation),
    [EField.HasGardenShed]: bool(AMENITY_NAMES.hasGardenShed),
    [EField.HasPool]: bool(AMENITY_NAMES.hasPool),
    [EField.NumberOfParkingsOutside]: num(
      AMENITY_NAMES.numberOfParkingsOutside,
    ),

    // Interior
    [EField.NumberOfToilets]: num(AMENITY_NAMES.numberOfToilets),
    [EField.NumberOfShowerRooms]: num(AMENITY_NAMES.numberOfShowerRooms),
    [EField.HasAttic]: bool(AMENITY_NAMES.hasAttic),
    [EField.BuildingHasStorage]: bool(AMENITY_NAMES.buildingHasStorage),
    [EField.IsFurnished]: bool(AMENITY_NAMES.isFurnished),
    [EField.KitchenType]: str(AMENITY_NAMES.kitchenType),
    [EField.FloorType]: str(AMENITY_NAMES.floorType),
    [EField.BuildingHasHardMaterials]: bool(
      AMENITY_NAMES.buildingHasHardMaterials,
    ),
    [EField.HasOven]: bool(AMENITY_NAMES.hasOven),
    [EField.HasFridge]: bool(AMENITY_NAMES.hasFridge),
    [EField.HasDishWasher]: bool(AMENITY_NAMES.hasDishwasher),
    [EField.HasRangeHood]: bool(AMENITY_NAMES.hasRangeHood),
    [EField.RoomCountGarage]: num(AMENITY_NAMES.roomCountGarage),
    [EField.NumberOfParkingsInside]: num(AMENITY_NAMES.numberOfParkingsInside),

    // Comfort
    [EField.HasLift]: bool(AMENITY_NAMES.hasLift),
    [EField.HasAirConditioning]: bool(AMENITY_NAMES.hasAirConditioning),
    [EField.HasAlarm]: bool(AMENITY_NAMES.hasAlarm),
    [EField.HasVideophone]: bool(AMENITY_NAMES.hasVideophone),
    [EField.HasSmokeDetectors]: bool(AMENITY_NAMES.hasSmokeDetectors),
    [EField.AccessibleForDisabled]: str(AMENITY_NAMES.accessibleForDisabled),

    // Energy
    [EField.HasCentralHeating]: bool(AMENITY_NAMES.hasCentralHeating),
    [EField.HeatingType]: str(AMENITY_NAMES.heatingType),
    [EField.HeatingDistribution]: str(AMENITY_NAMES.heatingDistribution),
    [EField.HasDoubleGlazing]: bool(AMENITY_NAMES.hasDoubleGlazing),
    [EField.GlazingType]: str(AMENITY_NAMES.glazingType),
    [EField.WindowFrameMaterial]: str(AMENITY_NAMES.windowFrameMaterial),
    [EField.HasInsulation]: bool(AMENITY_NAMES.hasInsulation),
    [EField.HasSolarPanels]: bool(AMENITY_NAMES.hasSolarPanels),
    [EField.IsLowEnergy]: bool(AMENITY_NAMES.isLowEnergy),
    [EField.IsPassiveHouse]: bool(AMENITY_NAMES.isPassiveHouse),
    [EField.Co2Emission]: num(AMENITY_NAMES.co2Emission),

    // House rules
    [EField.PetsAllowed]: str(AMENITY_NAMES.petsAllowed),
    [EField.PetsAllowedDetail]: str(AMENITY_NAMES.petsAllowedDetail),
    [EField.SmokingAllowed]: str(AMENITY_NAMES.smokingAllowed),

    // Condition and surroundings
    [EField.Condition]: str(AMENITY_NAMES.condition),
    [EField.ConstructionYear]: property.constructionYear,
    [EField.RenovationYear]: num(AMENITY_NAMES.renovationYear),
    [EField.EnvironmentType]: str(AMENITY_NAMES.environmentType),

    // Structural
    [EField.BuildingAdjoined]: str(AMENITY_NAMES.buildingAdjoined),
    [EField.FrontWidth]: num(AMENITY_NAMES.frontWidth),
    [EField.Volume]: num(AMENITY_NAMES.volume),
    [EField.RoofType]: str(AMENITY_NAMES.roofType),
    [EField.RoofMaterial]: str(AMENITY_NAMES.roofMaterial),

    // Risk and certificates
    [EField.FloodZoneGScore]: str(AMENITY_NAMES.floodZoneGScore),
    [EField.FloodZonePScore]: str(AMENITY_NAMES.floodZonePScore),
    [EField.ConformityCertificate]: str(AMENITY_NAMES.conformityCertificate),
    [EField.ElectricityCertificate]: str(AMENITY_NAMES.electricityCertificate),
    [EField.AsbestosCertificate]: str(AMENITY_NAMES.asbestosCertificate),
    [EField.AsbestosCertificateDate]: parseAmenityDate(
      str(AMENITY_NAMES.asbestosCertificateDate),
    ),

    // Media
    [EField.VirtualTourUrl]: str(AMENITY_NAMES.virtualTourUrl),
    [EField.VideoUrl]: str(AMENITY_NAMES.videoUrl),
    [EField.Model3dUrl]: str(AMENITY_NAMES.model3dUrl),
    [EField.FloorPlanUrl]: str(AMENITY_NAMES.floorPlanUrl),
  };
};

export const propertyTypeCanHaveFloors = (params: { property: IProperty }) => {
  const { property } = params;
  const { typeId } = property;

  switch (typeId) {
    case EPropertyTypeId.Apartment:
    case EPropertyTypeId.StudentResidence:
    case EPropertyTypeId.Commercial:
    case EPropertyTypeId.ParkingLot:
    case EPropertyTypeId.Garage:
    case EPropertyTypeId.Office:
      return true;

    case EPropertyTypeId.Group:
    case EPropertyTypeId.House:
    case EPropertyTypeId.Warehouse:
    case EPropertyTypeId.Shed:
    case EPropertyTypeId.Storage:
      return false;
  }
};

export const propertyTypeCanHaveAdjoinedType = (params: {
  property: IProperty;
}) => {
  const { property } = params;
  const { typeId } = property;

  switch (typeId) {
    case EPropertyTypeId.House:
    case EPropertyTypeId.Commercial:
    case EPropertyTypeId.Garage:
    case EPropertyTypeId.Office:
    case EPropertyTypeId.Warehouse:
    case EPropertyTypeId.Shed:
    case EPropertyTypeId.Storage:
      return true;

    case EPropertyTypeId.Group:
    case EPropertyTypeId.Apartment:
    case EPropertyTypeId.StudentResidence:
    case EPropertyTypeId.ParkingLot:
      return false;
  }
};
